/**
 * Opening a map from a route.
 *
 * Opening is asynchronous, can fail, and can succeed with bad news — a document that came back
 * incomplete or damaged. All four states are modelled, because a route that only knows "loading"
 * and "ready" has nowhere to put "your map is here but two edits did not come back".
 *
 * The engine is closed when the route unmounts, which flushes and writes a final checkpoint. That
 * is the difference between the next open replaying two entries and replaying a thousand.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { openMap, randomIds, systemClock, type OpenedMap } from '@orbit-mind/map-engine';
import type { AppError, MapId, UserId, WorkspaceId } from '@orbit-mind/shared-types';

import { localStorageLayer, trackBackgroundWrite, whenStorageIsIdle } from './storage';

export type OpenMapState =
  | { readonly phase: 'opening' }
  | { readonly phase: 'ready'; readonly opened: OpenedMap }
  | { readonly phase: 'failed'; readonly error: AppError };

/**
 * The author of every edit, until there are accounts.
 *
 * Phase 2 replaces this with the signed-in Orbit Mind account. It is a constant rather than a
 * random per-session id so that a journal written today still attributes correctly tomorrow.
 */
export const LOCAL_AUTHOR = 'local' as UserId;

/** The workspace every local map belongs to, until there are accounts to own one. */
export const LOCAL_WORKSPACE = 'local' as WorkspaceId;

export interface OpenedMapHandle {
  readonly state: OpenMapState;
  /**
   * Closes what is open and opens it again from storage.
   *
   * One path rather than two: adopting a document that came from somewhere else — a restore from
   * the server's history — writes it as a checkpoint and then re-opens, which is exactly what a
   * cold open does. Rewriting the live document underneath an editor whose undo stack points at
   * the old one is the kind of clever nobody can debug afterwards.
   */
  readonly reload: () => void;
  /**
   * Work to finish before the map closes: the last save of a password-protected map, written into
   * this engine from the one in memory. Awaited before `close()`, so it lands in the journal.
   */
  readonly beforeClose: (work: (() => Promise<void>) | null) => void;
}

export function useOpenMap(mapId: MapId): OpenedMapHandle {
  const [state, setState] = useState<OpenMapState>({ phase: 'opening' });
  const [reloadToken, setReloadToken] = useState(0);
  const pendingBeforeClose = useRef<(() => Promise<void>) | null>(null);
  const beforeClose = useCallback((work: (() => Promise<void>) | null) => {
    pendingBeforeClose.current = work;
  }, []);

  const reload = useCallback(() => {
    setState({ phase: 'opening' });
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = (): boolean => cancelled;
    let opened: OpenedMap | null = null;

    const run = async (): Promise<void> => {
      await whenStorageIsIdle();
      if (isCancelled()) return;
      const layer = localStorageLayer();
      const result = await openMap({
        storage: layer.journal,
        mapId,
        environment: { clock: systemClock(), ids: randomIds(), authorId: LOCAL_AUTHOR },
        // The library's row carries a title, a node count and a last-edited time so a listing
        // renders without opening a document. They are a cache of the document, and a checkpoint
        // is the moment the document was written down anyway — so it is the moment to refresh
        // them. Without this the library keeps saying "1 node" about a map with forty.
        // Returned, not fired and forgotten: the engine awaits it, so `close()` resolving means
        // the listing row is written too.
        onCheckpoint: async (document) => {
          await layer.catalog.save(document);
        },
      });

      if (!result.ok) {
        if (!isCancelled()) setState({ phase: 'failed', error: result.error });
        return;
      }

      opened = result.value;

      // Strict Mode mounts, unmounts and mounts again. Closing an engine the route has already
      // discarded is the difference between one live engine and two writing to one journal.
      if (isCancelled()) {
        trackBackgroundWrite(result.value.engine.close());
        return;
      }

      setState({ phase: 'ready', opened: result.value });
    };

    void run();

    const finishing = pendingBeforeClose;
    return () => {
      cancelled = true;
      // Tracked rather than awaited: an unmount cleanup cannot wait. The screen that reads what
      // this writes waits instead, through `whenStorageIsIdle`.
      if (opened !== null) {
        const engine = opened.engine;
        const work = finishing.current;
        finishing.current = null;
        trackBackgroundWrite(
          (async () => {
            try {
              await work?.();
            } finally {
              await engine.close();
            }
          })(),
        );
      }
    };
  }, [mapId, reloadToken]);

  return { state, reload, beforeClose };
}
